import { useEffect } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Widget from 'components/Dashboard/Widget';
import { useKPIGetDeliveredCars } from 'api/kpi/kpi';
import { KPIsWidgetEnum, LongTitles } from '../../utils';
import useManagementKpiFilter from './useManagementKpiFilter';
import {
  ControlFigures,
  MANAGEMENT_COLORS,
  nf,
  tableStyle,
  tdNumStyle,
  tdStyle,
  thNumStyle,
  thStyle,
  totalRowStyle,
  WidgetState,
} from './shared';

/**
 * P1 och P2 i ledningsrapporten — antal levererade bilar per ärendetyp.
 *
 * Årstrenden är samma endpoint med en annan gruppering, så widgeten visar tabellen per
 * rapportrad och dessutom en stapel per period när intervallet spänner över flera perioder.
 */
const DeliveredCarsWidget = () => {
  const { isReady, isDateRangeTooLong, filter, deps } = useManagementKpiFilter();
  const getData = useKPIGetDeliveredCars();

  useEffect(() => {
    if (isReady) {
      getData.mutate({ data: filter });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const result = getData.data;
  const dataPoints = result?.dataPoints ?? [];

  // Auktion summeras in i Inköp vid presentation — kategorin finns kvar i svaret för den dag
  // raden ska brytas ut, men rapporten visar rapportraden.
  const byRow = new Map<string, number>();
  dataPoints.forEach((dp) => {
    byRow.set(dp.reportRowName, (byRow.get(dp.reportRowName) ?? 0) + dp.deliveredCars);
  });
  const rows = Array.from(byRow.entries())
    .map(([name, cars]) => ({ name, cars }))
    .sort((a, b) => b.cars - a.cars);

  const byPeriod = new Map<string, number>();
  dataPoints.forEach((dp) => {
    const key = new Date(dp.date).toISOString().slice(0, 10);
    byPeriod.set(key, (byPeriod.get(key) ?? 0) + dp.deliveredCars);
  });
  const trend = Array.from(byPeriod.entries())
    .map(([date, cars]) => ({ date, cars }))
    .sort((a, b) => a.date.localeCompare(b.date));

  const total = result?.totalDeliveredCars ?? 0;

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.DeliveredCars]}
      count={total}
      isLoading={getData.isLoading}
      info="Antal levererade bilar per ärendetyp. En bil räknas den period ärendet stängdes, vilket är när sista uppdraget godkändes. Auktionsbilar ingår i Inköp."
    >
      <WidgetState
        isLoading={getData.isLoading}
        isDateRangeTooLong={isDateRangeTooLong}
        isEmpty={rows.length === 0}
      >
        {trend.length > 1 ? (
          <div style={{ width: '100%', height: 220, marginBottom: '15px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trend}>
                <CartesianGrid stroke="#eee" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: number) => [`${nf(v)} bilar`, 'Levererade']} />
                <Bar dataKey="cars" fill={MANAGEMENT_COLORS[0]} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : null}

        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Ärendetyp</th>
              <th style={thNumStyle}>Antal bilar</th>
              <th style={thNumStyle}>Andel</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.name} style={{ borderBottom: '1px solid #eee' }}>
                <td style={tdStyle}>{row.name}</td>
                <td style={tdNumStyle}>{nf(row.cars)}</td>
                <td style={tdNumStyle}>
                  {total > 0 ? `${nf((row.cars / total) * 100, 1)} %` : '–'}
                </td>
              </tr>
            ))}
            <tr style={totalRowStyle}>
              <td style={tdStyle}>Totalt</td>
              <td style={tdNumStyle}>{nf(total)}</td>
              <td style={tdNumStyle}>100 %</td>
            </tr>
          </tbody>
        </table>

        <ControlFigures
          items={[
            result && result.openCasesOlderThanPeriod > 0
              ? `${nf(result.openCasesOlderThanPeriod)} öppna ärenden äldre än perioden syns inte i siffran`
              : null,
            result && result.otherCaseTypes.length > 0
              ? `Övrigt består av ${result.otherCaseTypes.length} ärendetyper`
              : null,
          ]}
        />
      </WidgetState>
    </Widget>
  );
};

export default DeliveredCarsWidget;
